const BASE = "/api/chat";

export interface ChatConversation {
  id: string;
  assessmentId: string | null;
  userId: string | null;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessage?: string;
  lastMessageAt?: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  inputTokens: number | null;
  outputTokens: number | null;
  createdAt: string;
}

export interface SendMessageResponse {
  message: ChatMessage;
  quotaWarning?: boolean;
  percentUsed?: number;
}

export async function listConversations(assessmentId: string): Promise<ChatConversation[]> {
  const res = await fetch(`${BASE}/conversations?assessmentId=${encodeURIComponent(assessmentId)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load conversations");
  return res.json();
}

export async function createConversation(assessmentId: string): Promise<ChatConversation> {
  const res = await fetch(`${BASE}/conversations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ assessmentId }),
  });
  if (!res.ok) throw new Error("Failed to create conversation");
  return res.json();
}

export async function getMessages(conversationId: string): Promise<ChatMessage[]> {
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load messages");
  return res.json();
}

export async function sendMessage(
  conversationId: string,
  content: string,
  assessmentId: string,
  cardContext?: string
): Promise<{ data?: SendMessageResponse; quotaExceeded?: boolean; error?: string }> {
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ content, assessmentId, cardContext }),
  });

  if (res.status === 403) {
    const body = await res.json();
    if (body.error === "quota_exceeded") return { quotaExceeded: true };
    return { error: body.error };
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return { error: body.error ?? "AI service temporarily unavailable. Try again later." };
  }

  return { data: await res.json() };
}

export async function getQuotaUsage(): Promise<{ percentUsed: number }> {
  const res = await fetch(`${BASE}/quota`, { credentials: "include" });
  if (!res.ok) return { percentUsed: 0 };
  return res.json();
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await fetch(`${BASE}/conversations/${conversationId}`, {
    method: "DELETE",
    credentials: "include",
  });
}
